import React, { useState } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import PodcastHeader from "./PodcastHeader";

// Import Icons
import {
  FaThLarge, FaMicrophone, FaUsers, FaUserCircle, FaCog, FaSignOutAlt, FaBook, FaHeadphones
} from "react-icons/fa";

const PodcastLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to log out?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, log out",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("token");
        localStorage.removeItem("userData");
        localStorage.removeItem("userRole");
        navigate("/podcast/login");
        Swal.fire("Logged Out", "You have been logged out.", "success");
      }
    });
  };

  const onNavigate = (path) => {
    navigate(`/podcast/${path}`);
    setIsSidebarOpen(false);
  };

  // Menu items structure for Podcast Platform
  const menuItems = [
    {
      title: "Main",
      titleColor: "text-gray-600",
      items: [
        {
          path: "dashboard",
          icon: FaThLarge,
          label: "Dashboard",
          gradient: "from-blue-400 to-blue-600",
          iconColor: "text-blue-500"
        }
      ]
    },
    {
      title: "Podcast Studio",
      titleColor: "text-gray-600",
      items: [
        {
          path: "studio-register",
          icon: FaMicrophone,
          label: "Studio Registration",
          gradient: "from-purple-400 to-purple-600",
          iconColor: "text-purple-500"
        }
      ]
    },
    {
      title: "Content",
      titleColor: "text-gray-600",
      items: [
        {
          path: "podcast-book",
          icon: FaBook,
          label: "Podcast Booking",
          gradient: "from-indigo-400 to-indigo-600",
          iconColor: "text-indigo-500"
        },
        {
          path: "booking-history",
          icon: FaUsers,
          label: "Booking History",
          gradient: "from-purple-400 to-purple-600",
          iconColor: "text-purple-500"
        }
      ]
    },
    {
      title: "Account",
      titleColor: "text-gray-600",
      items: [
        {
          path: "profile",
          icon: FaUserCircle,
          label: "Profile",
          gradient: "from-emerald-400 to-emerald-600",
          iconColor: "text-emerald-500"
        },
        {
          path: "settings",
          icon: FaCog,
          label: "Settings",
          gradient: "from-gray-400 to-gray-600",
          iconColor: "text-gray-500"
        }
      ]
    }
  ];

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Sidebar Toggle Button for Mobile */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center w-10 h-10"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? "✕" : "☰"}
      </button>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-[85%] max-w-[320px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:w-80`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 h-16 flex items-center justify-center rounded-tr-3xl lg:rounded-tr-none">
            <div className="flex items-center space-x-2">
              <FaHeadphones className="text-white text-2xl" />
              <h2 className="text-2xl font-bold text-white">
                Podcast Studio
              </h2>
            </div>
            <button 
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700" 
              onClick={() => setIsSidebarOpen(false)}
            >
              ✕
            </button>
          </div>

          {/* Scrollable Sidebar Content */}
          <div className="overflow-y-auto h-[calc(100vh-4rem)] px-3 py-4 sm:px-4 sm:py-6 custom-scrollbar">
            {menuItems.map((section, idx) => (
              <div key={idx} className="mb-5 sm:mb-6">
                <h2 className={`font-bold uppercase text-xs tracking-wider mb-2 sm:mb-3 px-2 ${section.titleColor}`}>
                  {section.title}
                </h2>
                {section.items.map((item, itemIdx) => {
                  const isActive = location.pathname === `/podcast/${item.path}`;
                  return (
                    <div
                      key={itemIdx}
                      onClick={() => onNavigate(item.path)}
                      className={`
                        flex items-center space-x-3 px-3 sm:px-4 py-3 rounded-xl cursor-pointer
                        transition-all duration-300 transform hover:scale-105 mb-1 sm:mb-2
                        ${isActive
                          ? `bg-gradient-to-r ${item.gradient} shadow-lg`
                          : 'hover:bg-gray-100'
                        }
                        group touch-action-manipulation
                      `}
                    >
                      <div className={`
                        transition-all duration-300
                        ${isActive
                          ? 'text-white'
                          : `${item.iconColor} group-hover:scale-110`
                        }
                      `}>
                        <item.icon className="w-5 h-5" />
                      </div>
                      <span className={`
                        font-medium transition-all duration-300 text-sm sm:text-base
                        ${isActive ? 'text-white' : 'text-gray-700'}
                      `}>
                        {item.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
            {/* Logout button */}
            <div 
              onClick={handleLogout}
              className="flex items-center space-x-3 px-3 sm:px-4 py-3 rounded-xl cursor-pointer mt-4 sm:mt-6 bg-gray-100 hover:bg-gray-200 transition-all duration-300 touch-action-manipulation"
            >
              <div className="text-red-500">
                <FaSignOutAlt className="w-5 h-5" />
              </div>
              <span className="font-medium text-red-500 text-sm sm:text-base">Logout</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
        {/* PodcastHeader for dashboard */}
        <PodcastHeader />
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          ></div>
        )}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 md:p-6">
          <Outlet />
        </main>
      </div>
      {/* Help Support Button */}
      <button
        onClick={() => navigate("/help")}
        className="fixed bottom-4 right-4 bg-purple-500 text-white rounded-full px-6 py-3 shadow-lg hover:bg-purple-700 transition duration-150 ease-in-out"
      >
        Help Support
      </button>
    </div>
  );
};

export default PodcastLayout;